import { startCase } from 'lodash';
import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import PencilIcon from 'svg/PencilIcon';

import { VALUE_NAMES } from '../constants';

// The "Eligibility checked with the following details" summary card shown atop every slideout
// state. Reads the check inputs off the workflow values and offers the Edit Details entry point.

interface Props {
  workflow: Workflow;
  onEditDetails: () => void;
}

export default function EligibilityDetailsHeader({ workflow, onEditDetails }: Props) {
  const dob = simpleDate(workflow.valueFor(VALUE_NAMES.PATIENT_DOB)?.value);
  const genderRaw = workflow.valueFor(VALUE_NAMES.PATIENT_GENDER)?.value;
  const payerName = workflow.valueFor(VALUE_NAMES.PLAN_TYPE)?.value;
  const memberId = workflow.valueFor(VALUE_NAMES.MEMBER_ID)?.value;

  const fields: { label: string; value: string }[] = [
    { label: 'Name', value: workflow.patientName },
    { label: 'DOB', value: dob || '—' },
    { label: 'Gender', value: genderRaw ? startCase(genderRaw) : '—' },
    { label: 'Payer Name', value: payerName || '—' },
    { label: 'Member ID', value: memberId || '—' },
  ];

  return (
    <Card data-testid='eligibility-details-header'>
      <TopRow>
        <Heading>Eligibility checked with the following details:</Heading>
        <EditLink type='button' onClick={onEditDetails} data-testid='edit-details'>
          <PencilIcon color='transparent' stroke={colors.primaryBlue} strokeWidth={1.5} width={14} height={14} />
          Edit Details
        </EditLink>
      </TopRow>
      <Fields>
        {fields.map((field) => (
          <Field key={field.label}>
            <FieldLabel>{field.label}</FieldLabel>
            <FieldValue>{field.value}</FieldValue>
          </Field>
        ))}
      </Fields>
    </Card>
  );
}

const Card = styled.div`
  border: 1px solid ${colors.black15};
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Heading = styled(BodySmallBold)`
  color: ${colors.black};
`;

const EditLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.primaryBlue};
  font-weight: 600;
  flex-shrink: 0;
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px 40px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled(BodySmall)`
  color: ${colors.black50};
`;

const FieldValue = styled(BodySmall)`
  color: ${colors.black};
`;
